export function sleep(ms) {
  return new Promise((r) => setTimeout(r, ms))
}

export function isMobile() {
  const userAgent = navigator.userAgent || navigator.vendor || window.opera

  // iOS detection
  if (/iPhone|iPad|iPod/i.test(userAgent)) {
    return true
  }

  // iPadOS can report a desktop-like Macintosh user agent.
  if (/Macintosh/i.test(userAgent) && navigator.maxTouchPoints > 1) {
    return true
  }

  // Android detection
  if (/Android/i.test(userAgent)) {
    return true
  }

  // Other mobile device detection
  if (/webOS|BlackBerry|IEMobile|Opera Mini/i.test(userAgent)) {
    return true
  }

  // If none of the above, assume desktop
  return false
}

const IN_APP_BROWSER_PATTERN =
  /KAKAOTALK|NAVER|Instagram|FBAN|FBAV|FB_IAB|DaumApps|Line\/|Toss|Twitter|Snapchat|;\s*wv\)|\bwv\b/i

function getBrowserUserAgent(userAgent) {
  return (
    userAgent ||
    (typeof navigator !== "undefined" && (navigator.userAgent || navigator.vendor)) ||
    (typeof window !== "undefined" && window.opera) ||
    ""
  )
}

export function isIOSDevice(userAgent, maxTouchPoints) {
  const currentUserAgent = getBrowserUserAgent(userAgent)
  const currentTouchPoints =
    typeof maxTouchPoints === "number"
      ? maxTouchPoints
      : typeof navigator !== "undefined"
      ? navigator.maxTouchPoints || 0
      : 0

  return /iPhone|iPad|iPod/i.test(currentUserAgent) || (/Macintosh/i.test(currentUserAgent) && currentTouchPoints > 1)
}

export function isInAppBrowser(userAgent) {
  return IN_APP_BROWSER_PATTERN.test(getBrowserUserAgent(userAgent))
}

export function isChromeBrowser(userAgent, browserBrands) {
  const currentUserAgent = getBrowserUserAgent(userAgent)

  if (isInAppBrowser(currentUserAgent)) {
    return false
  }

  const currentBrands =
    browserBrands ||
    (!userAgent && typeof navigator !== "undefined" && navigator.userAgentData && navigator.userAgentData.brands) ||
    []

  if (currentBrands.some(({ brand }) => /Google Chrome/i.test(brand))) {
    return true
  }

  if (/CriOS\//i.test(currentUserAgent)) {
    return true
  }

  if (!/Chrome\//i.test(currentUserAgent)) {
    return false
  }

  return !/(EdgA?|EdgiOS|OPR|Opera|SamsungBrowser|DuckDuckGo|YaBrowser|Vivaldi|Whale)/i.test(
    currentUserAgent
  )
}

export function isSafariBrowser(userAgent) {
  const currentUserAgent = getBrowserUserAgent(userAgent)

  if (isInAppBrowser(currentUserAgent)) {
    return false
  }

  return (
    /Safari\//i.test(currentUserAgent) &&
    !/(CriOS|Chrome|Chromium|FxiOS|Firefox|EdgiOS|EdgA?|OPiOS|OPR|DuckDuckGo|YaBrowser|Vivaldi|Whale)/i.test(
      currentUserAgent
    )
  )
}

export function shouldShowBrowserWarning(userAgent, browserBrands) {
  return !isChromeBrowser(userAgent, browserBrands) && !isSafariBrowser(userAgent)
}

export const loadImage = async (url) => {
  return new Promise((resolve, reject) => {
    const image = new Image()
    image.setAttribute("crossorigin", "anonymous")
    image.src = url
    image.onload = () => {
      resolve(image)
    }
    image.onerror = (e) => {
      reject(e)
    }
  })
}

export const resizeImage = async (image, width, height) => {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement("canvas")
    const ctx = canvas.getContext("2d")
    canvas.width = width
    canvas.height = height
    ctx.drawImage(image, 0, 0, width, height)
    resolve(canvas.toDataURL())
  })
}

export const scaleImage = async (image, maxWidth) => {
  const aspectRatio = image.width / image.height
  const maxHeight = maxWidth / aspectRatio
  return resizeImage(image, maxWidth, maxHeight)
}

/**
 * 이메일 형식을 검증하고 유효하지 않으면 기본 이메일 반환
 * @param {string} email - 검증할 이메일 주소
 * @param {string} defaultEmail - 유효하지 않을 경우 반환할 기본 이메일 (기본값: 'support@rocketworks.io')
 * @returns {string} 유효한 이메일 주소
 */
export function getValidPayerEmail(email, defaultEmail = 'support@rocketworks.io') {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return email && emailRegex.test(email) ? email : defaultEmail
}

/**
 * 다국어 JSON 값에서 현재 언어에 맞는 값을 반환
 * @param {string|object} value - JSON 문자열 또는 객체 형태의 다국어 값
 * @param {string} lang - 현재 언어 ('ko', 'en', 'jp')
 * @returns {string} 현재 언어에 맞는 값, 없으면 한글(kr), JSON이 아니면 원본 값
 */
export function getMultiLangValue(value, lang = 'ko') {
  if (!value) return ''

  try {
    const parsed = typeof value === 'string' ? JSON.parse(value) : value

    if (typeof parsed === 'object' && parsed !== null) {
      // 현재 언어에 맞는 값 반환, 없으면 한글
      if (lang === 'en' && parsed.en) {
        return parsed.en
      } else if (lang === 'jp' && parsed.jp) {
        return parsed.jp
      } else if (parsed.kr) {
        return parsed.kr
      }
      // 모든 다국어 값이 없으면 원본 반환
      return value
    }
    return value
  } catch (e) {
    // JSON이 아니면 해당 값 그대로 표시
    return value
  }
}

/**
 * 사용자/직원 이름을 표시 형식으로 변환
 * JSON 형식(다국어)이면 한글(kr) 이름을 반환, 아니면 원본 값 반환
 * @param {string|object} name - 사용자 이름 (JSON 문자열 또는 일반 문자열)
 * @returns {string} 표시할 이름
 */
export function getUserDisplayName(name) {
  if (!name) return ''

  try {
    const parsed = typeof name === 'string' ? JSON.parse(name) : name
    if (typeof parsed === 'object' && parsed !== null && parsed.kr) {
      return parsed.kr
    }
    return name
  } catch (e) {
    // JSON이 아니면 DB값 그대로 표시
    return name
  }
}
